/**
 * WebSocket client for ExchangeThread realtime.
 *
 * Lifecycle:
 *   1. Mint a single-use ticket (REST: GET /api/threads/:id/ws-ticket)
 *   2. Open `wss://<origin>/api/threads/:id/ws` and pass the ticket via the
 *      `Sec-WebSocket-Protocol` subprotocol header as `wwaave.ticket.<value>`.
 *      Subprotocols sit in the upgrade request headers, not the URL, so they
 *      don't land in CF edge logs — fixes H3 (ticket-in-query replay window).
 *   3. Server upgrade is forwarded to ThreadDO via HMAC handshake.
 *   4. On disconnect: exponential backoff (1s start, 30s ceiling, 25% jitter).
 *      Each reconnect re-mints a fresh ticket — old ones are single-use and
 *      have likely expired anyway (30s TTL).
 *
 * Catch-up after reconnect:
 *   The DO does not buffer messages across hibernation. Subscribers should
 *   refetch the thread on the `connected` event to fill any gap.
 */

export type ThreadEvent =
  | { type: 'message_posted'; messageId: string; threadId: string }
  | { type: 'system_message_posted'; messageId: string; threadId: string }
  | { type: 'message_edited'; messageId: string; body: string; editedAt: number; threadId: string }
  | { type: 'message_read'; messageId: string; userId: string; readAt: number; threadId: string }
  | { type: 'state_changed'; exchangeId: string; state: string; threadId: string }
  | { type: 'thread_read'; userId: string; readAt: number; threadId: string }
  | { type: 'typing'; userId: string; t: number }
  | { type: 'pong'; t: number }
  | { type: 'connected' }
  | { type: 'disconnected'; code: number; reason: string };

export type ThreadClient = {
  /** Subscribe to all incoming events. Returns an unsubscribe fn. */
  subscribe: (cb: (ev: ThreadEvent) => void) => () => void;
  /** Send an outbound typing indicator. No-op if socket isn't open. */
  sendTyping: () => void;
  /** Send a heartbeat ping. The DO replies with a `pong`. */
  ping: () => void;
  /** Close the connection and stop reconnection attempts. */
  close: () => void;
  /** True if the underlying socket is currently open. */
  isOpen: () => boolean;
};

const BACKOFF_START_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const BACKOFF_JITTER = 0.25;

export function connectThread(threadId: string): ThreadClient {
  const subscribers = new Set<(ev: ThreadEvent) => void>();
  let socket: WebSocket | null = null;
  let backoffMs = BACKOFF_START_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function emit(ev: ThreadEvent) {
    for (const cb of subscribers) {
      try {
        cb(ev);
      } catch (err) {
        // A subscriber crash shouldn't take down the socket.
        console.error('[ws-client] subscriber threw', err);
      }
    }
  }

  async function connect() {
    if (stopped) return;
    try {
      // Mint a ticket. The mint endpoint requires a session cookie; if it
      // 401s the user is signed out and we should stop trying.
      const res = await fetch(`/api/threads/${encodeURIComponent(threadId)}/ws-ticket`, {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (res.status === 401) {
        stopped = true;
        emit({ type: 'disconnected', code: 4401, reason: 'unauthenticated' });
        return;
      }
      if (!res.ok) {
        throw new Error(`ticket mint failed: ${res.status}`);
      }
      const { ticket } = (await res.json()) as { ticket: string; expiresAt: number };

      // Build wss:// or ws:// based on current location. The ticket rides in
      // the Sec-WebSocket-Protocol header (see comment at top of file).
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${location.host}/api/threads/${encodeURIComponent(
        threadId,
      )}/ws`;

      // The browser WebSocket API serialises the subprotocol array into the
      // Sec-WebSocket-Protocol request header. The server MUST echo the
      // selected protocol back, else the browser drops the connection with a
      // protocol-mismatch error.
      const ws = new WebSocket(url, [`wwaave.ticket.${ticket}`]);
      socket = ws;

      ws.addEventListener('open', () => {
        backoffMs = BACKOFF_START_MS;
        emit({ type: 'connected' });
      });
      ws.addEventListener('message', (e) => {
        if (typeof e.data !== 'string') return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(e.data);
        } catch {
          return;
        }
        if (typeof parsed !== 'object' || parsed === null || !('type' in parsed)) return;
        // The DO forwards the broadcast payload as-is. We trust the type field
        // because this was server-signed before fanout.
        emit(parsed as ThreadEvent);
      });
      ws.addEventListener('close', (e) => {
        socket = null;
        emit({ type: 'disconnected', code: e.code, reason: e.reason });
        scheduleReconnect();
      });
      ws.addEventListener('error', () => {
        // The browser also fires close after error; let the close handler
        // do the reconnect bookkeeping.
      });
    } catch (err) {
      console.warn('[ws-client] connect failed', err);
      scheduleReconnect();
    }
  }

  function scheduleReconnect() {
    if (stopped) return;
    if (reconnectTimer !== null) return;
    const jitter = backoffMs * BACKOFF_JITTER * (Math.random() * 2 - 1);
    const wait = Math.max(250, Math.round(backoffMs + jitter));
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      backoffMs = Math.min(BACKOFF_MAX_MS, Math.round(backoffMs * 2));
      void connect();
    }, wait);
  }

  // Kick off the initial connect on the next tick so callers can subscribe
  // synchronously and not miss the first event.
  queueMicrotask(() => {
    void connect();
  });

  return {
    subscribe(cb) {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
    sendTyping() {
      if (socket !== null && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'typing' }));
        } catch {
          /* swallow */
        }
      }
    },
    ping() {
      if (socket !== null && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'ping' }));
        } catch {
          /* swallow */
        }
      }
    },
    close() {
      stopped = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close(1000, 'client closed');
        } catch {
          /* swallow */
        }
        socket = null;
      }
    },
    isOpen() {
      return socket !== null && socket.readyState === WebSocket.OPEN;
    },
  };
}
